// Venues API endpoints
import apiClient, { ApiError } from './apiClient';
import type {
  Venue,
  VenueEvent,
  BottleCategory,
  BottleServicePackage,
  BottleServicePayload,
  VenueMenu,
  Review,
  FAQ,
  InstagramMediaItem,
  Tag,
  TagCategory,
  VenueFilterRequest,
  PaginatedResponse,
  VenueListQueryParams,
  ReservationRequest,
  Reservation,
  AvailabilityRequest,
  AvailabilityResponse,
  BookingRequest,
  Booking,
  BottleOrderRequest,
  BottleOrder,
  BottleOrderSubmitRequest,
  BottleOrderDetail,
  GiftCardRequest,
  GiftCard,
  ContactRequest,
  CareerApplicationRequest,
  TalentSubmissionRequest,
  ReviewRequest,
  TicketOrderRequest,
  TicketOrder,
} from '@/types/api';

// Helper to extract data from wrapped response
// The API returns data directly (e.g. { id, name, ... }) or sometimes wrapped ({ data: { ... } })
function unwrapResponse<T>(response: unknown): T {
  if (response === null || response === undefined) {
    return response as T;
  }
  
  if (typeof response !== 'object') {
    return response as T;
  }

  const obj = response as Record<string, unknown>;
  
  // 1. Check for success: false in the response
  if (obj.success === false) {
    const message = (obj.message as string) || 'Request failed';
    throw new Error(message);
  }
  
  // 2. Extract from "data" property if present
  // The API often returns { success: true, data: { ...venue fields... } }
  // or { success: true, data: { data: { ...venue fields... } } }
  if ('data' in obj) {
    const dataContent = obj.data;

    if (dataContent && typeof dataContent === 'object') {
      const innerObj = dataContent as Record<string, unknown>;

      // Check for nested data: data.data
      if ('data' in innerObj && innerObj.data !== undefined) {
        return innerObj.data as T;
      }

      // If it doesn't have nested data but has fields like id/slug/name, it's the resource
      return innerObj as T;
    }

    return dataContent as T;
  }
  
  return response as T;
}

/** Public venue payload may expose promo videos under alternate keys or row shapes. */
function normalizePublicVenueVideos(venue: Venue): Venue["videos"] {
  const r = venue as unknown as Record<string, unknown>;
  const keys = ["videos", "venue_videos", "venueVideos", "promotional_videos"] as const;
  const out: NonNullable<Venue["videos"]> = [];
  const seen = new Set<string>();

  for (const k of keys) {
    const rows = r[k];
    if (!Array.isArray(rows)) continue;
    for (const item of rows) {
      if (typeof item !== "object" || item === null) continue;
      const sig = JSON.stringify(item);
      if (seen.has(sig)) continue;
      seen.add(sig);

      const o = item as Record<string, unknown>;
      const link =
        (typeof o.link === "string" && o.link.trim()) ||
        (typeof o.url === "string" && o.url.trim()) ||
        (typeof o.video_url === "string" && o.video_url.trim()) ||
        "";
      const embedLink =
        (typeof o.embedLink === "string" && o.embedLink.trim()) ||
        (typeof o.embed_link === "string" && o.embed_link.trim()) ||
        undefined;
      if (!link && !embedLink) continue;
      out.push(embedLink ? { link: link || "", embedLink } : { link });
    }
  }

  return out.length > 0 ? out : venue.videos;
}

/** Normalize snake_case and claim fields from public venue API. */
function normalizePublicVenue(venue: Venue): Venue {
  const r = venue as unknown as Record<string, unknown>;
  const ownerRaw = venue.ownerEmail ?? r.owner_email;
  const ownerEmail = typeof ownerRaw === "string" ? ownerRaw : venue.ownerEmail;
  const domainRaw = venue.domain ?? r.domain;
  const domain = typeof domainRaw === "string" ? domainRaw : venue.domain;
  let verified: boolean | undefined = venue.verified;
  if (verified === undefined && typeof r.verified === "boolean") {
    verified = r.verified;
  }
  const videos = normalizePublicVenueVideos(venue);
  return {
    ...venue,
    ...(ownerEmail !== undefined ? { ownerEmail } : {}),
    ...(domain !== undefined ? { domain } : {}),
    verified,
    ...(videos != null ? { videos } : {}),
  };
}

/** DELETE /venue/steps/tickets/{id} (ticket or event id per API); fallback …/events/{id} on 404. */
async function deleteVenueStepTicketOrEvent(resourceId: string | number): Promise<void> {
  const id = encodeURIComponent(String(resourceId));
  try {
    await apiClient.delete(`/venue/steps/tickets/${id}`, { requireAuth: true });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      await apiClient.delete(`/venue/steps/events/${id}`, { requireAuth: true });
      return;
    }
    throw err;
  }
}

export const venuesApi = {
  // ==================== Public Venue Endpoints ====================

  /**
   * Get paginated list of venues (`GET /vl/venues`).
   * Pass a single params object, or legacy positional (page, limit, bounds).
   *
   * Bounds format: comma-separated south,north,west,east (matching API docs).
   */
  getVenues: async (
    paramsOrPage?: VenueListQueryParams | number,
    limitArg?: number,
    boundsArg?: string
  ): Promise<PaginatedResponse<Venue>> => {
    let params: VenueListQueryParams;
    if (typeof paramsOrPage === 'number' || paramsOrPage === undefined) {
      const page = paramsOrPage ?? 1;
      const limit = limitArg ?? 20;
      params = {
        page,
        limit,
        ...(boundsArg ? { bounds: boundsArg } : {}),
      };
    } else {
      params = paramsOrPage;
    }

    const q = new URLSearchParams();
    q.set("page", String(params.page));
    q.set("limit", String(params.limit));
    if (params.bounds?.trim()) q.set("bounds", params.bounds.trim());
    if (params.near?.trim()) q.set("near", params.near.trim());
    if (params.country?.trim()) q.set("country", params.country.trim());
    if (params.search?.trim()) q.set("name", params.search.trim());
    if (params.sort?.trim()) q.set("sort", params.sort.trim());

    const endpoint = `/vl/venues?${q.toString()}`;
    const rawResponse = await apiClient.get(endpoint);
    const response = rawResponse as Record<string, unknown>;

    // Shape A: { data: Venue[], pagination: { ... } }
    if ("data" in response && Array.isArray(response.data) && response.pagination) {
      const pg = response.pagination as Record<string, unknown>;
      const perPage =
        (pg.per_page as number) ??
        (pg.perPage as number) ??
        (pg.limit as number) ??
        params.limit;
      const total = (pg.totalItems as number) ?? (pg.total as number) ?? 0;
      const currentPage = (pg.page as number) ?? 1;
      const totalPagesRaw =
        (pg.totalPages as number) ?? (pg.total_pages as number) ?? null;
      const last_page =
        totalPagesRaw !== null && totalPagesRaw > 0
          ? totalPagesRaw
          : total > 0 && perPage > 0
            ? Math.max(1, Math.ceil(total / perPage))
            : 1;

      return {
        data: (response.data as Venue[]) || [],
        current_page: currentPage,
        last_page,
        per_page: perPage,
        total,
      };
    }

    // Handle nested pagination structure
    let paginatedData = response as Record<string, unknown>;
    if ('data' in response && response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      const inner = response.data as Record<string, unknown>;
      if ('current_page' in inner || 'data' in inner) {
        paginatedData = inner;
      }
    }

    return {
      data: (paginatedData.data as Venue[]) || [],
      current_page: (paginatedData.current_page as number) || 1,
      last_page: (paginatedData.last_page as number) || 1,
      per_page: (paginatedData.per_page as number) || params.limit,
      total: (paginatedData.total as number) || 0,
    };
  },

  /**
   * Countries that currently have venues (`GET /vl/countries`), A → Z.
   *
   * Backed by `App\Http\Controllers\VL\VenueController::getVenueCountries`. Powers the
   * `/explore` and `/search/map` country dropdowns so they only list countries with records.
   * Returns an empty array when the API has nothing usable — callers decide the fallback.
   */
  getCountries: async (): Promise<string[]> => {
    const rawResponse = await apiClient.get('/vl/countries');
    const data = unwrapResponse<unknown>(rawResponse);
    if (!Array.isArray(data)) return [];

    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of data) {
      // Accept both `["United States"]` and `[{ country: "United States", venues: 128 }]`.
      const raw =
        typeof row === 'string'
          ? row
          : row && typeof row === 'object'
            ? (row as Record<string, unknown>).country
            : null;
      const country = typeof raw === 'string' ? raw.trim() : '';
      if (!country || seen.has(country)) continue;
      seen.add(country);
      out.push(country);
    }

    return out.sort((a, b) => a.localeCompare(b));
  },

  /**
   * Filter venues by criteria
   */
  filterVenues: async (filters: VenueFilterRequest): Promise<Venue[]> => {
    const rawResponse = await apiClient.post('/vl/filter', filters);
    return unwrapResponse<Venue[]>(rawResponse);
  },

  /**
   * Get venue details by slug
   */
  getVenue: async (slug: string): Promise<Venue> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}`);
    const data = unwrapResponse<Venue>(rawResponse);
    return normalizePublicVenue(data);
  },

  /**
   * Get venue tags/preferences
   */
  getVenueTags: async (slug: string): Promise<TagCategory> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/tags`);
    return unwrapResponse<TagCategory>(rawResponse);
  },

  /**
   * Get venue menus
   */
  getMenus: async (slug: string): Promise<VenueMenu[]> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/menus`);
    return unwrapResponse<VenueMenu[]>(rawResponse);
  },

  /**
   * Get venue bottle service
   */
  getBottles: async (slug: string): Promise<BottleServicePayload> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/bottles`);
    // Bottles endpoint has multiple shapes; keep both bottles and packages when present.
    if (Array.isArray(rawResponse)) {
      return { bottles: rawResponse as BottleCategory[], packages: [] };
    }
    if (rawResponse && typeof rawResponse === 'object') {
      const obj = rawResponse as Record<string, unknown>;

      // Shape A: { bottles: [...], packages: [...] }
      if (Array.isArray(obj.bottles)) {
        return {
          bottles: obj.bottles as BottleCategory[],
          packages: (Array.isArray(obj.packages) ? obj.packages : []) as BottleServicePackage[],
        };
      }

      // Shape B: { data: [...], packages: [...] }
      if (Array.isArray(obj.data)) {
        return {
          bottles: obj.data as BottleCategory[],
          packages: (Array.isArray(obj.packages) ? obj.packages : []) as BottleServicePackage[],
        };
      }

      // Shape C: { data: { bottles: [...], packages: [...] } }
      if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
        const dataObj = obj.data as Record<string, unknown>;
        if (Array.isArray(dataObj.bottles) || Array.isArray(dataObj.packages)) {
          return {
            bottles: (Array.isArray(dataObj.bottles) ? dataObj.bottles : []) as BottleCategory[],
            packages: (Array.isArray(dataObj.packages) ? dataObj.packages : []) as BottleServicePackage[],
          };
        }
      }
    }

    // Final fallback to previous behavior.
    const unwrapped = unwrapResponse<unknown>(rawResponse);
    if (Array.isArray(unwrapped)) {
      return { bottles: unwrapped as BottleCategory[], packages: [] };
    }
    if (unwrapped && typeof unwrapped === 'object') {
      const u = unwrapped as Record<string, unknown>;
      return {
        bottles: (Array.isArray(u.bottles) ? u.bottles : []) as BottleCategory[],
        packages: (Array.isArray(u.packages) ? u.packages : []) as BottleServicePackage[],
      };
    }
    return { bottles: [], packages: [] };
  },

  /**
   * Get venue events
   */
  getEvents: async (slug: string): Promise<VenueEvent[]> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/events`);
    return unwrapResponse<VenueEvent[]>(rawResponse);
  },

  /**
   * Get venue past events
   */
  getPastEvents: async (slug: string): Promise<VenueEvent[]> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/events/past-events`);
    return unwrapResponse<VenueEvent[]>(rawResponse);
  },

  /**
   * Get venue reviews
   */
  getReviews: async (slug: string): Promise<Review[]> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/reviews`);
    return unwrapResponse<Review[]>(rawResponse);
  },

  /**
   * Get venue FAQs
   */
  getFAQs: async (slug: string): Promise<FAQ[]> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/faq`);
    return unwrapResponse<FAQ[]>(rawResponse);
  },

  /**
   * Get a venue's recent Instagram media (public — only populated if the venue
   * owner has connected their Instagram account via the Social step)
   */
  getInstagramMedia: async (slug: string): Promise<{
    connected: boolean;
    username: string | null;
    items: InstagramMediaItem[];
  }> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/instagram-media`);
    return unwrapResponse(rawResponse);
  },

  // ==================== Reservation Operations ====================

  /**
   * Check reservation availability
   */
  checkAvailability: async (
    slug: string,
    data: AvailabilityRequest
  ): Promise<AvailabilityResponse> => {
    return apiClient.post(`/vl/venue/${slug}/reservation/check-availability`, data);
  },

  /**
   * Make a reservation
   */
  makeReservation: async (
    slug: string,
    data: ReservationRequest
  ): Promise<Reservation> => {
    return apiClient.post(`/vl/venue/${slug}/reservation`, data, { requireAuth: false });
  },

  // ==================== Booking Operations ====================

  /**
   * Book a venue
   */
  bookVenue: async (slug: string, data: BookingRequest): Promise<Booking> => {
    return apiClient.post(`/vl/venue/${slug}/booking`, data, { requireAuth: false });
  },

  // ==================== Bottle Service Operations ====================

  /**
   * Create bottle order
   */
  createBottleOrder: async (
    slug: string,
    data: BottleOrderRequest
  ): Promise<BottleOrder> => {
    const rawResponse = await apiClient.post(`/vl/venue/${slug}/bottles/order`, data);
    return unwrapResponse<BottleOrder>(rawResponse);
  },

  /**
   * Submit bottle order with client info
   */
  submitBottleOrder: async (
    slug: string,
    orderId: string,
    data: BottleOrderSubmitRequest
  ): Promise<BottleOrder> => {
    const rawResponse = await apiClient.put(`/vl/venue/${slug}/bottles/order/${orderId}`, data, { requireAuth: false });
    return unwrapResponse<BottleOrder>(rawResponse);
  },

  /**
   * Get bottle order by id (checkout / order summary).
   * Uses member auth when available; some venues may allow unauthenticated read for the order id.
   */
  getBottleOrder: async (slug: string, orderId: string): Promise<BottleOrderDetail> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/bottles/order/${orderId}`, {
      requireAuth: false,
    });
    return unwrapResponse<BottleOrderDetail>(rawResponse);
  },

  // ==================== Event/Ticket Operations ====================

  /**
   * Create ticket order
   */
  createTicketOrder: async (
    slug: string,
    data: TicketOrderRequest
  ): Promise<TicketOrder> => {
    const rawResponse = await apiClient.post(`/vl/venue/${slug}/events/tickets`, data, { requireAuth: true });
    return unwrapResponse<TicketOrder>(rawResponse);
  },

  /**
   * Get ticket order info
   */
  getTicketOrder: async (slug: string, orderId: string): Promise<TicketOrder> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/events/booking/${orderId}`, { requireAuth: true });
    return unwrapResponse<TicketOrder>(rawResponse);
  },

  /**
   * Update ticket order info
   */
  updateTicketOrder: async (
    slug: string,
    orderId: string,
    data: {
      clientName: string;
      clientEmail: string;
      clientGender?: string;
      clientAddress?: string;
      clientRequest?: string;
      clientPhoneNumber: string;
      clientBirthDate?: string;
      id: string;
    }
  ): Promise<TicketOrder> => {
    return apiClient.put(`/vl/venue/${slug}/events/booking/${orderId}`, data, { requireAuth: true });
  },

  /**
   * Get ticket guests info
   */
  getTicketGuests: async (slug: string, orderId: string): Promise<TicketOrder> => {
    return apiClient.get(`/vl/venue/${slug}/events/booking/${orderId}/guests`, { requireAuth: true });
  },

  /**
   * Update ticket guests info
   */
  updateTicketGuests: async (
    slug: string,
    orderId: string,
    data: { id: string; attendees: unknown[] }
  ): Promise<TicketOrder> => {
    return apiClient.put(`/vl/venue/${slug}/events/booking/${orderId}/guests`, data, { requireAuth: true });
  },

  // ==================== Gift Card Operations ====================

  /**
   * Purchase gift card
   */
  purchaseGiftCard: async (slug: string, data: GiftCardRequest): Promise<GiftCard> => {
    const rawResponse = await apiClient.post(`/vl/venue/${slug}/gift-card`, data, { requireAuth: true });
    return unwrapResponse<GiftCard>(rawResponse);
  },

  /**
   * Get gift card info
   */
  getGiftCard: async (slug: string, orderId: string): Promise<GiftCard> => {
    const rawResponse = await apiClient.get(`/vl/venue/${slug}/gift-card/${orderId}`, { requireAuth: true });
    return unwrapResponse<GiftCard>(rawResponse);
  },

  // ==================== Contact Operations ====================

  /**
   * Send contact message to venue
   */
  sendContact: async (slug: string, data: ContactRequest): Promise<{ message: string }> => {
    return apiClient.post(`/vl/venue/${slug}/contact`, data, { requireAuth: false });
  },

  // ==================== Career Operations ====================

  /**
   * Apply for job at venue
   */
  applyForJob: async (slug: string, data: CareerApplicationRequest): Promise<{ message: string }> => {
    const rawResponse = await apiClient.post(`/vl/venue/${slug}/career`, data, { requireAuth: false });
    const unwrapped = unwrapResponse<{ message?: string } | string>(rawResponse);
    if (typeof unwrapped === "string") return { message: unwrapped };
    return { message: unwrapped?.message ?? "Application submitted successfully." };
  },

  // ==================== Talent Operations ====================

  /**
   * Submit talent application
   */
  submitTalent: async (slug: string, data: TalentSubmissionRequest): Promise<{ message: string }> => {
    return apiClient.post(`/vl/venue/${slug}/talent`, data, { requireAuth: false });
  },

  // ==================== Review Operations ====================

  /**
   * Add review to venue
   */
  addReview: async (slug: string, data: ReviewRequest): Promise<Review> => {
    return apiClient.post(`/vl/venue/${slug}/reviews`, data, { requireAuth: true });
  },

  // ==================== Venue Owner Operations ====================

  /**
   * Get the authenticated venue owner's venue info
   */
  getMyVenue: async (): Promise<Venue> => {
    const rawResponse = await apiClient.get<Venue | { data: Venue }>('/venue', { requireAuth: true });
    return unwrapResponse<Venue>(rawResponse);
  },

  /**
   * Update venue info (step: info)
   */
  updateVenueInfo: async (data: Record<string, unknown>): Promise<unknown> => {
    return apiClient.post('/venue/steps/info', data, { requireAuth: true });
  },

  /**
   * Get venue step data
   */
  getVenueStep: async (step: string): Promise<unknown> => {
    const rawResponse = await apiClient.get(`/venue/steps/${step}`, { requireAuth: true });
    return unwrapResponse<unknown>(rawResponse);
  },

  /**
   * Update venue step data
   */
  updateVenueStep: async (step: string, data: unknown): Promise<unknown> => {
    return apiClient.post(`/venue/steps/${step}`, data, { requireAuth: true });
  },

  /**
   * Save venue owner events (POST /venue/steps/events).
   * Tries a raw JSON array first (common for step endpoints); on 400/422 retries `{ events }` like menus.
   */
  saveVenueEvents: async (events: unknown[]): Promise<unknown> => {
    try {
      return await apiClient.post('/venue/steps/events', events, { requireAuth: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 422 || err.status === 400)) {
        return await apiClient.post('/venue/steps/events', { events }, { requireAuth: true });
      }
      throw err;
    }
  },

  /**
   * Delete a venue event (saved on server) by id.
   * @see deleteVenueStepTicketOrEvent
   */
  deleteVenueEvent: deleteVenueStepTicketOrEvent,

  /**
   * Delete a ticket tier under an event (same DELETE route as production).
   */
  deleteVenueTicket: deleteVenueStepTicketOrEvent,

  /**
   * Delete a promotional video by ID
   */
  deleteVenueVideo: async (videoId: number): Promise<void> => {
    await apiClient.delete(`/venue/steps/video/${videoId}`, { requireAuth: true });
  },

  deleteVenueRoom: async (roomId: string | number): Promise<void> => {
    await apiClient.delete(`/venue/steps/room/${roomId}`, { requireAuth: true });
  },

  /** Clears a saved room's image or floor plan. */
  deleteVenueRoomFile: async (roomId: string | number, field: "image" | "floor"): Promise<void> => {
    await apiClient.delete(`/venue/steps/room/${roomId}/${field}`, { requireAuth: true });
  },

  deleteVenueLogo: async (): Promise<void> => {
    await apiClient.delete(`/venue/steps/logo`, { requireAuth: true });
  },

  /**
   * Delete a menu by ID
   */
  deleteVenueMenu: async (menuId: number): Promise<void> => {
    await apiClient.delete(`/venue/steps/menu/${menuId}`, { requireAuth: true });
  },

  /**
   * Delete a bottle service package by ID
   */
  deleteVenuePackage: async (packageId: number): Promise<void> => {
    await apiClient.delete(`/venue/steps/package/${packageId}`, { requireAuth: true });
  },

  /**
   * Delete a bottle brand by ID
   */
  deleteVenueBrand: async (brandId: number): Promise<void> => {
    await apiClient.delete(`/venue/steps/bottle/${brandId}`, { requireAuth: true });
  },

  /**
   * Delete a FAQ by ID
   */
  deleteVenueFaq: async (faqId: number): Promise<void> => {
    await apiClient.delete(`/venue/steps/faq/${faqId}`, { requireAuth: true });
  },

  /** Hide/unhide one of the venue owner's own reviews from the public profile — text/rating stay untouched. */
  updateVenueReviewStatus: async (reviewId: number, status: 'published' | 'archived'): Promise<unknown> => {
    return apiClient.put(`/venue/steps/reviews/${reviewId}`, { status }, { requireAuth: true });
  },

  /**
   * Get all available venue tags (for tag selection in forms)
   */
  getAllVenueTags: async (): Promise<Record<string, Array<{ id: number; name: string }>>> => {
    const rawResponse = await apiClient.get('/venue/tags', { requireAuth: false });
    return unwrapResponse<Record<string, Array<{ id: number, name: string }>>>(rawResponse);
  },

  // ==================== Stripe Connect Operations ====================

  /**
   * Get Stripe connect info for the venue
   */
  getStripeConnectInfo: async (): Promise<{
    chargesEnabled?: boolean;
    details_submitted?: boolean;
    payouts_enabled?: boolean;
    [key: string]: unknown;
  } | null> => {
    try {
      const rawResponse = await apiClient.get('/stripe/connect-info', { requireAuth: true });
      return unwrapResponse<{
        chargesEnabled?: boolean;
        details_submitted?: boolean;
        payouts_enabled?: boolean;
        [key: string]: unknown;
      }>(rawResponse);
    } catch {
      // If 404 or error, venue is not connected yet
      return null;
    }
  },

  /**
   * Get Stripe account link URL for onboarding or dashboard access
   */
  getStripeAccountLink: async (type: 'venue' = 'venue'): Promise<{ url: string }> => {
    const rawResponse = await apiClient.get(`/stripe/account-link?type=${type}`, { requireAuth: true });
    return unwrapResponse<{ url: string }>(rawResponse);
  },

  /**
   * Disconnect the venue's linked Stripe account
   */
  disconnectStripe: async (): Promise<{ message?: string }> => {
    return apiClient.post('/stripe/disconnect', {}, { requireAuth: true });
  },

  /**
   * Public: whether a venue accepts card (Stripe) payments — used client-side to decide
   * whether to offer the "Card" tab at all.
   */
  getStripePublicConnectInfo: async (venueId: string): Promise<{ enabled: boolean }> => {
    const rawResponse = await apiClient.get(`/stripe/public-connect-info/${encodeURIComponent(venueId)}`);
    return unwrapResponse(rawResponse);
  },

  // ==================== PayPal Connect Operations ====================

  /**
   * Get PayPal connect info for the venue
   */
  getPaypalConnectInfo: async (): Promise<{
    connected?: boolean;
    completed?: boolean;
    [key: string]: unknown;
  } | null> => {
    try {
      const rawResponse = await apiClient.get('/paypal/connect-info', { requireAuth: true });
      return unwrapResponse<{
        connected?: boolean;
        completed?: boolean;
        [key: string]: unknown;
      }>(rawResponse);
    } catch {
      // If 404 or error, venue is not connected yet
      return null;
    }
  },

  /**
   * Get PayPal onboarding (Partner Referral) link
   */
  getPaypalAccountLink: async (): Promise<{ url: string }> => {
    const rawResponse = await apiClient.get('/paypal/account-link', { requireAuth: true });
    return unwrapResponse<{ url: string }>(rawResponse);
  },

  /**
   * Disconnect the venue's linked PayPal account
   */
  disconnectPaypal: async (): Promise<{ message?: string }> => {
    return apiClient.post('/paypal/disconnect', {}, { requireAuth: true });
  },

  /**
   * Complete PayPal onboarding after returning from the Partner Referral flow.
   * `queryString` is the browser's `location.search` (carries `merchantIdInPayPal` / `merchantId`).
   */
  confirmPaypalConnect: async (queryString: string): Promise<{ completed?: boolean }> => {
    const rawResponse = await apiClient.post(`/paypal/connect${queryString}`, {}, { requireAuth: true });
    return unwrapResponse<{ completed?: boolean }>(rawResponse);
  },

  // ==================== Instagram Connect Operations ====================

  /**
   * Get Instagram connect info for the venue
   */
  getInstagramConnectInfo: async (): Promise<{
    connected?: boolean;
    completed?: boolean;
    username?: string | null;
  } | null> => {
    try {
      const rawResponse = await apiClient.get('/instagram/connect-info', { requireAuth: true });
      return unwrapResponse(rawResponse);
    } catch {
      return null;
    }
  },

  /**
   * Get the Instagram OAuth authorize URL to open in a popup
   */
  getInstagramAccountLink: async (): Promise<{ url: string }> => {
    const rawResponse = await apiClient.get('/instagram/account-link', { requireAuth: true });
    return unwrapResponse<{ url: string }>(rawResponse);
  },

  /**
   * Exchange the OAuth `code` (returned to the popup callback page) for a connected account
   */
  connectInstagram: async (code: string): Promise<{ username?: string | null }> => {
    const rawResponse = await apiClient.post('/instagram/connect', { code }, { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Disconnect the venue's linked Instagram account
   */
  disconnectInstagram: async (): Promise<{ message?: string }> => {
    return apiClient.post('/instagram/disconnect', {}, { requireAuth: true });
  },
};

export default venuesApi;
